import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Dayjs } from 'dayjs';
import { useAppSelector } from 'store/hooks';
import { RootState } from 'store/index';
import styles from './RangeDatePicker.module.scss';

interface RangeDatePickerProps {
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onFromChange: (newValue: Dayjs | null) => void;
  onToChange: (newValue: Dayjs | null) => void;
  className?: string;
  disableConstraints?: boolean;
}

const RangeDatePicker: React.FC<RangeDatePickerProps> = ({
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  className = '',
  disableConstraints = false,
}) => {
  const theme = useAppSelector((state: RootState) => state.ui.theme);
  const [localFrom, setLocalFrom] = useState<Dayjs | null>(fromDate);
  const [localTo, setLocalTo] = useState<Dayjs | null>(toDate);

  const fromIso = fromDate?.toISOString() || '';
  const toIso = toDate?.toISOString() || '';

  useEffect(() => {
    setLocalFrom(fromDate);
  }, [fromIso]);

  useEffect(() => {
    setLocalTo(toDate);
  }, [toIso]);

  const innerSx = {
    '& .MuiInputBase-input': {
      color: theme === 'dark' ? '#ffffff !important' : '#111827 !important',
      WebkitTextFillColor: theme === 'dark' ? '#ffffff !important' : '#111827 !important',
    },
    '& .MuiPickersInputBase-root': {
      color: theme === 'dark' ? '#ffffff !important' : '#111827 !important',
    },
    '& .MuiInputBase-root': {
      color: theme === 'dark' ? '#ffffff !important' : '#111827 !important',
    },
    '& .MuiSvgIcon-root': {
      color: theme === 'dark' ? '#8698a6' : '#4b5563',
    },
    '& .MuiInputLabel-root': {
      color: theme === 'dark' ? '#8698a6 !important' : '#4b5563 !important',
    },
    '&:hover .MuiInputLabel-root': {
      color: theme === 'dark' ? '#ffffff !important' : '#111827 !important',
    },
  };

  return (
    <Box className={`${styles.rangeContainer} ${className}`}>
      <DateTimePicker
        label="From"
        value={localFrom}
        maxDate={disableConstraints ? undefined : localTo || undefined}
        format="DD/MM/YYYY HH:mm"
        onChange={setLocalFrom}
        onAccept={onFromChange}
        slotProps={{
          actionBar: {
            actions: ['clear', 'cancel', 'accept'],
          },
          textField: {
            size: 'small',
            className: styles.picker,
            sx: innerSx,
          },
        }}
      />

      <DateTimePicker
        label="To"
        value={localTo}
        minDate={disableConstraints ? undefined : localFrom || undefined}
        format="DD/MM/YYYY HH:mm"
        onChange={setLocalTo}
        onAccept={onToChange}
        slotProps={{
          actionBar: {
            actions: ['clear', 'cancel', 'accept'],
          },
          textField: {
            size: 'small',
            className: styles.picker,
            sx: innerSx,
          },
        }}
      />
    </Box>
  );
};

export default RangeDatePicker;
